import { useEffect, useMemo, useRef, useState } from "react";
import { api, authedUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EmployeeCombobox from "@/components/EmployeeCombobox";
import LookupCombo from "@/components/LookupCombo";
import TagInput from "@/components/TagInput";
import { Upload, User as UserIcon, Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useBranches } from "@/components/BranchBits";
import { useAuth } from "@/context/AuthContext";

const EMPLOYMENT_TYPES = [
  { v: "full_time", l: "Full-Time" },
  { v: "part_time", l: "Part-Time" },
  { v: "contract", l: "On Contract" },
  { v: "internship", l: "Internship" },
  { v: "trainee", l: "Trainee" },
];

const empty = {
  full_name: "", emp_code: "", designation: "", department: "", email: "", phone: "",
  date_of_joining: "", date_of_birth: "", gender: "", reporting_manager_id: "",
  address: "", about: "", skills: [], employment_type: "full_time",
  marital_status: "", marriage_anniversary: "", probation_end_date: "",
  notice_period_start: "", notice_period_end: "", is_active: true, avatar_url: "",
  role: "employee", branch_id: "",
  password: "",
};

export default function EmployeeFormDialog({ open, onOpenChange, employee, onSaved }) {
  const { branches } = useBranches();
  const { user: me } = useAuth();
  const pinned = me?.role === "branch_admin";   // Branch Admins: own branch, plain employee role (server enforces too)
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [colleagues, setColleagues] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const fileRef = useRef(null);

  const hasLogin = !!employee?.user_id;

  useEffect(() => {
    if (!open) return;
    // Never carry over a password value from a loaded employee record — the
    // field always starts empty. Empty on submit means "don't touch login".
    setForm(employee ? { ...empty, ...employee, password: "" } : empty);
    api.get("/employees").then((r) => setColleagues(r.data.filter((e) => e.id !== employee?.id)));
  }, [open, employee]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["full_name", "emp_code", "designation", "department", "email", "phone", "date_of_joining", "date_of_birth"];
    for (const k of required) {
      if (!form[k]) { toast.error(`Please fill ${k.replace(/_/g, " ")}`); return; }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Clean empty strings for optional selects
      ["gender", "marital_status", "reporting_manager_id", "marriage_anniversary",
       "probation_end_date", "notice_period_start", "notice_period_end", "avatar_url"].forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      // Password: only send if the admin actually typed something
      if (!payload.password || !payload.password.trim()) {
        delete payload.password;
      } else if (payload.password.length < 8) {
        toast.error("Password must be at least 8 characters");
        setSaving(false);
        return;
      }
      if (employee?.id) {
        await api.patch(`/employees/${employee.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", payload);
        toast.success("Employee created");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadAvatar = async (file) => {
    if (!file || !employee?.id) {
      if (!employee?.id) toast.error("Save the employee first to upload an avatar");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/employees/${employee.id}/avatar`, fd);
      set("avatar_url", r.data.url);
      toast.success("Avatar uploaded");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const avatarSrc = useMemo(() => (form.avatar_url?.startsWith("/api/") ? authedUrl(form.avatar_url) : form.avatar_url), [form.avatar_url]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="emp-form-title">{employee ? "Edit Employee" : "New Employee"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={avatarSrc} />
              <AvatarFallback><UserIcon className="w-6 h-6" /></AvatarFallback>
            </Avatar>
            <div>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadAvatar(e.target.files?.[0])} />
              <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="emp-avatar-upload">
                <Upload className="w-4 h-4 mr-1" />{uploading ? "Uploading…" : "Upload picture"}
              </Button>
              {!employee?.id && <div className="text-[10px] text-muted-foreground mt-1">Save the employee first, then upload a picture</div>}
            </div>
          </div>

          <Tabs defaultValue="basic">
            <TabsList className="mb-3">
              <TabsTrigger value="basic" data-testid="tab-basic">Basic</TabsTrigger>
              <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
              <TabsTrigger value="employment" data-testid="tab-employment">Employment</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-3 mt-0">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Full Name *</Label><Input data-testid="ef-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
                <div><Label>EMP ID *</Label><Input data-testid="ef-code" value={form.emp_code} onChange={(e) => set("emp_code", e.target.value)} placeholder="EMP-1024" /></div>
                <div><Label>Designation *</Label><LookupCombo testid="ef-desig" kind="designations" value={form.designation} onChange={(v) => set("designation", v)} placeholder="Select designation…" /></div>
                <div><Label>Department *</Label><LookupCombo testid="ef-dept" kind="departments" value={form.department} onChange={(v) => set("department", v)} placeholder="Select department…" /></div>
                <div><Label>Email *</Label><Input data-testid="ef-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} disabled={!!employee} /></div>
                <div><Label>Phone *</Label><Input data-testid="ef-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                <div>
                  <Label>Reporting To</Label>
                  <EmployeeCombobox
                    testid="ef-manager" employees={colleagues}
                    value={form.reporting_manager_id}
                    onChange={(v) => set("reporting_manager_id", v)}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.is_active ? "active" : "inactive"} onValueChange={(v) => set("is_active", v === "active")}>
                    <SelectTrigger data-testid="ef-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!pinned && <div>
                  <Label>Role</Label>
                  <Select value={form.role || "employee"} onValueChange={(v) => set("role", v)}>
                    <SelectTrigger data-testid="ef-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="branch_admin">Branch Admin</SelectItem>
                      <SelectItem value="admin">Company Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-muted-foreground mt-1">Company Admins have full access to every branch and module. Branch Admins see everything but only manage records they create.</div>
                </div>}
                {!pinned && <div>
                  <Label>Branch</Label>
                  <Select value={form.branch_id || "__none"} onValueChange={(v) => set("branch_id", v === "__none" ? "" : v)}>
                    <SelectTrigger data-testid="ef-branch"><SelectValue placeholder="No branch" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">No branch</SelectItem>
                      {branches.filter((b) => b.is_active !== false).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.city ? ` · ${b.city}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-muted-foreground mt-1">Home branch. Required for Branch Admins; doesn't limit task assignment.</div>
                </div>}
              </div>
              <div>
                <Label>About</Label>
                <Textarea data-testid="ef-about" rows={3} value={form.about} onChange={(e) => set("about", e.target.value)} placeholder="Short bio, seniority context…" />
              </div>
              <div>
                <Label>Skills</Label>
                <TagInput testid="ef-skills" value={form.skills || []} onChange={(v) => set("skills", v)} placeholder="React, Python, Kafka…" />
              </div>

              {/* Login credentials */}
              <div className="pt-2 border-t border-border/40">
                <div className="flex items-center gap-2 mb-1.5">
                  <KeyRound className="w-3.5 h-3.5 text-muted-foreground" />
                  <Label className="mb-0">
                    {employee ? (hasLogin ? "Reset login password" : "Create login for this employee") : "Login password"}
                  </Label>
                </div>
                <div className="relative">
                  <Input
                    data-testid="ef-password"
                    type={showPw ? "text" : "password"}
                    autoComplete="new-password"
                    value={form.password || ""}
                    onChange={(e) => set("password", e.target.value)}
                    placeholder={employee && hasLogin ? "Leave blank to keep current password" : "Set a login password (min 8 chars)"}
                    className="pr-9"
                  />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {employee
                    ? (hasLogin
                        ? "Sets a new password for this employee's login. Leave blank to keep the current one."
                        : "Setting a password here creates a login account. Share it with them out-of-band.")
                    : "Optional. If provided, the employee can sign in with their email + this password. Minimum 8 characters."}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="personal" className="space-y-3 mt-0">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date of Birth *</Label><Input type="date" data-testid="ef-dob" value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} /></div>
                <div>
                  <Label>Gender</Label>
                  <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
                    <SelectTrigger data-testid="ef-gender"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Marital Status</Label>
                  <Select value={form.marital_status || ""} onValueChange={(v) => set("marital_status", v)}>
                    <SelectTrigger data-testid="ef-marital"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Marriage Anniversary</Label>
                  <Input type="date" data-testid="ef-anniv" disabled={form.marital_status !== "married"} value={form.marriage_anniversary || ""} onChange={(e) => set("marriage_anniversary", e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Textarea data-testid="ef-address" rows={3} value={form.address || ""} onChange={(e) => set("address", e.target.value)} />
              </div>
            </TabsContent>

            <TabsContent value="employment" className="space-y-3 mt-0">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date of Joining *</Label><Input type="date" data-testid="ef-doj" value={form.date_of_joining || ""} onChange={(e) => set("date_of_joining", e.target.value)} /></div>
                <div>
                  <Label>Employment Type</Label>
                  <Select value={form.employment_type || "full_time"} onValueChange={(v) => set("employment_type", v)}>
                    <SelectTrigger data-testid="ef-emp-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EMPLOYMENT_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Probation End Date</Label><Input type="date" data-testid="ef-probation" value={form.probation_end_date || ""} onChange={(e) => set("probation_end_date", e.target.value)} /></div>
                <div />
                <div><Label>Notice Period Start</Label><Input type="date" data-testid="ef-np-start" value={form.notice_period_start || ""} onChange={(e) => set("notice_period_start", e.target.value)} /></div>
                <div><Label>Notice Period End</Label><Input type="date" data-testid="ef-np-end" value={form.notice_period_end || ""} onChange={(e) => set("notice_period_end", e.target.value)} /></div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="emp-save-btn">{saving ? "Saving…" : (employee ? "Save changes" : "Create employee")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
